/**
 * DateField — branded date picker with text-input fallback.
 *
 * Replaces the native `<input type="date">` with a typed input plus a popover
 * calendar that matches our design tokens (Forest / Amber / Mint). Built
 * without Radix deps so it ships immediately.
 *
 * Behaviour:
 *   - Controlled input. `value` and `onChange` use the ISO `YYYY-MM-DD`
 *     format so existing `react-hook-form` registrations keep working
 *     unchanged.
 *   - User can either TYPE a date (DD/MM/YYYY, DD-MM-YYYY, or YYYY-MM-DD)
 *     OR click the calendar icon to open the popover and pick visually.
 *   - Typing fires onChange only when the input parses to a valid in-range
 *     date; otherwise the ISO value stays at its last good state and the
 *     input shows an invalid border.
 *   - Keyboard friendly: Esc closes the popover, focus trap returns to
 *     the input.
 *   - Respects `min` / `max` ISO date strings to disable out-of-range days
 *     and to validate typed input.
 */
import * as React from 'react';
import { createPortal } from 'react-dom';
import { FancySelect } from './fancy-select';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../lib/cn';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface DateFieldProps {
  value:    string;
  onChange: (v: string) => void;
  min?:     string;
  max?:     string;
  placeholder?: string;
  invalid?: boolean;
  disabled?: boolean;
  id?:      string;
  ariaLabel?: string;
  className?: string;
  /** Hide the calendar and accept only typed DD/MM/YYYY. Defaults to showing it. */
  calendar?: boolean;
}

const MONTHS_LONG = [
  'January', 'February', 'March',     'April',   'May',      'June',
  'July',    'August',   'September', 'October', 'November', 'December',
];
const DOW_SHORT = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

// ── Helpers ───────────────────────────────────────────────────────────────────

function parseISO(s: string): Date | null {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m || !m[1] || !m[2] || !m[3]) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return isNaN(d.getTime()) ? null : d;
}
function toISO(d: Date): string {
  const yyyy = d.getFullYear();
  const mm   = String(d.getMonth() + 1).padStart(2, '0');
  const dd   = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}
function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear()
      && a.getMonth()    === b.getMonth()
      && a.getDate()     === b.getDate();
}
/** Format an ISO date as the user-facing "DD/MM/YYYY" string (for the input). */
function toDisplayInput(d: Date): string {
  const dd   = String(d.getDate()).padStart(2, '0');
  const mm   = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

/**
 * Auto-format raw input as DD/MM/YYYY. Strips non-digits and inserts slashes
 * at positions 2 and 5 so the user only ever types digits.
 *
 * Examples:
 *   '2'        → '2'
 *   '25'       → '25'
 *   '255'      → '25/5'
 *   '25052026' → '25/05/2026'
 *   'abc25'    → '25'      (non-digits stripped)
 */
function autoFormat(input: string): string {
  const digits = input.replace(/\D/g, '').slice(0, 8); // DDMMYYYY max 8 digits
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

/**
 * Parse a (typed or formatted) date string into a JS Date.
 * Accepted shapes: DD/MM/YYYY, DD-MM-YYYY, YYYY-MM-DD (with `/` or `-`).
 * Returns null if not parseable.
 */
function parseTyped(input: string): Date | null {
  const s = input.trim();
  if (!s) return null;

  // DD/MM/YYYY or DD-MM-YYYY  (most common after auto-format).
  let m = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/.exec(s);
  if (m && m[1] && m[2] && m[3]) {
    const day  = Number(m[1]);
    const mon  = Number(m[2]);
    const year = Number(m[3]);
    if (mon < 1 || mon > 12) return null;
    const d = new Date(year, mon - 1, day);
    if (
      isNaN(d.getTime()) ||
      d.getDate() !== day || d.getMonth() !== mon - 1 || d.getFullYear() !== year
    ) return null;
    return d;
  }

  // YYYY-MM-DD or YYYY/MM/DD  (paste support).
  m = /^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/.exec(s);
  if (m && m[1] && m[2] && m[3]) {
    const year = Number(m[1]);
    const mon  = Number(m[2]);
    const day  = Number(m[3]);
    if (mon < 1 || mon > 12) return null;
    const d = new Date(year, mon - 1, day);
    if (
      isNaN(d.getTime()) ||
      d.getDate() !== day || d.getMonth() !== mon - 1 || d.getFullYear() !== year
    ) return null;
    return d;
  }

  return null;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function DateField({
  value,
  onChange,
  min,
  max,
  placeholder = 'DD / MM / YYYY',
  calendar = true,
  invalid     = false,
  disabled    = false,
  id,
  ariaLabel,
  className,
}: DateFieldProps) {
  const inputRef   = React.useRef<HTMLInputElement | null>(null);
  const popoverRef = React.useRef<HTMLDivElement | null>(null);
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = React.useState(false);
  // Flip the calendar above the input when there isn't room below (e.g. the
  // field sits low in the viewport or inside a bottom-anchored filter panel),
  // and cap its height to the available space so it is never clipped by the
  // viewport edge — it scrolls internally in the rare case neither side fits.
  const [openUp, setOpenUp] = React.useState(false);
  const [maxH, setMaxH] = React.useState<number | undefined>(undefined);
  /** Fixed coords for the portaled calendar, measured from the trigger. */
  const [pos, setPos] = React.useState<{ left: number; top?: number; bottom?: number } | null>(
    null,
  );

  const selected = parseISO(value);
  const minDate  = min ? parseISO(min) : null;
  const maxDate  = max ? parseISO(max) : null;

  // Local input string — separate from the ISO `value` so the user can type
  // partial values without them being clobbered by the parent on every render.
  const [inputText, setInputText] = React.useState<string>(
    selected ? toDisplayInput(selected) : '',
  );
  const [typingInvalid, setTypingInvalid] = React.useState(false);

  // Keep local text in sync when the external value changes (e.g. calendar pick,
  // form reset). We only overwrite while the user is NOT focusing the input.
  React.useEffect(() => {
    if (document.activeElement === inputRef.current) return;
    setInputText(selected ? toDisplayInput(selected) : '');
    setTypingInvalid(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const [viewMonth, setViewMonth] = React.useState<Date>(() =>
    selected ? startOfMonth(selected)
             : maxDate ? startOfMonth(maxDate)
             : startOfMonth(new Date()),
  );

  React.useEffect(() => {
    if (selected) setViewMonth(startOfMonth(selected));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Decide flip direction the moment the popover opens (and on resize/scroll),
  // by comparing space below vs. above the trigger against the calendar height.
  React.useLayoutEffect(() => {
    if (!open) return;
    const decide = () => {
      const wrap = wrapperRef.current;
      const pop = popoverRef.current;
      if (!wrap) return;
      const rect = wrap.getBoundingClientRect();
      const natural = pop?.scrollHeight ?? 340;
      const spaceBelow = window.innerHeight - rect.bottom - 12;
      const spaceAbove = rect.top - 12;
      const up = spaceBelow < natural && spaceAbove > spaceBelow;
      setOpenUp(up);
      // Never taller than the roomier side; overflow-y-auto handles the rest.
      setMaxH(Math.max(220, Math.floor(up ? spaceAbove : spaceBelow)));
      setPos({
        left: rect.left,
        ...(up ? { bottom: window.innerHeight - rect.top + 6 } : { top: rect.bottom + 6 }),
      });
    };
    decide();
    window.addEventListener('resize', decide);
    window.addEventListener('scroll', decide, true);
    return () => {
      window.removeEventListener('resize', decide);
      window.removeEventListener('scroll', decide, true);
    };
  }, [open]);

  React.useEffect(() => {
    if (!open) setPos(null);
  }, [open]);

  // A modal Dialog's scroll lock (react-remove-scroll) preventDefaults wheel
  // events outside the dialog, which would stop this body-portaled calendar
  // from scrolling. Its listener is bubble-phase, so stop the event here.
  const calendarMounted = pos !== null;
  React.useEffect(() => {
    const el = popoverRef.current;
    if (!open || !el) return;
    const stop = (e: Event) => e.stopPropagation();
    el.addEventListener('wheel', stop, { passive: false });
    el.addEventListener('touchmove', stop, { passive: false });
    return () => {
      el.removeEventListener('wheel', stop);
      el.removeEventListener('touchmove', stop);
    };
  }, [open, calendarMounted]);

  React.useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      const t = e.target as Node;
      if (
        wrapperRef.current && !wrapperRef.current.contains(t) &&
        popoverRef.current && !popoverRef.current.contains(t)
      ) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
        inputRef.current?.focus();
      }
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // ── Calendar grid ─────────────────────────────────────────────────────────

  const firstDayOfMonth = startOfMonth(viewMonth);
  const startWeekday    = firstDayOfMonth.getDay();
  const daysInMonth     = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 0).getDate();

  const cells: Array<{ date: Date; current: boolean }> = [];
  for (let i = startWeekday - 1; i >= 0; i--) {
    const d = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), -i);
    cells.push({ date: d, current: false });
  }
  for (let day = 1; day <= daysInMonth; day++) {
    cells.push({
      date:    new Date(viewMonth.getFullYear(), viewMonth.getMonth(), day),
      current: true,
    });
  }
  while (cells.length < 42) {
    const lastCell = cells[cells.length - 1];
    if (!lastCell) break;
    const last = lastCell.date;
    cells.push({
      date:    new Date(last.getFullYear(), last.getMonth(), last.getDate() + 1),
      current: false,
    });
  }

  const today = new Date();

  function dayDisabled(d: Date): boolean {
    if (minDate && d < minDate) return true;
    if (maxDate && d > maxDate) return true;
    return false;
  }

  function pick(d: Date) {
    if (dayDisabled(d)) return;
    const iso = toISO(d);
    onChange(iso);
    setInputText(toDisplayInput(d));
    setTypingInvalid(false);
    setOpen(false);
    inputRef.current?.focus();
  }

  // ── Typed-input handlers ─────────────────────────────────────────────────

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    // Auto-format digits → "DD/MM/YYYY". User never has to type slashes.
    const next = autoFormat(e.target.value);
    setInputText(next);

    if (!next) {
      // Cleared — propagate empty.
      onChange('');
      setTypingInvalid(false);
      return;
    }

    const parsed = parseTyped(next);
    if (parsed && !dayDisabled(parsed)) {
      onChange(toISO(parsed));
      setViewMonth(startOfMonth(parsed));
      setTypingInvalid(false);
    } else if (next.length < 10) {
      // Still typing — don't flag invalid yet, just hold last good ISO.
      setTypingInvalid(false);
    } else {
      // 10 chars typed but didn't parse → invalid date.
      setTypingInvalid(true);
    }
  }

  function handleInputKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    // Allow Backspace to skip over an auto-inserted `/` so the user deletes
    // the digit they expect rather than just removing the slash.
    if (e.key === 'Backspace') {
      const el  = e.currentTarget;
      const pos = el.selectionStart ?? 0;
      if (pos > 0 && inputText[pos - 1] === '/') {
        e.preventDefault();
        const without = inputText.slice(0, pos - 2) + inputText.slice(pos);
        const reformatted = autoFormat(without);
        setInputText(reformatted);
        // Move caret to the right place after the React re-render.
        requestAnimationFrame(() => {
          const np = Math.max(0, pos - 2);
          el.setSelectionRange(np, np);
        });
        if (!reformatted) {
          onChange('');
          setTypingInvalid(false);
        }
      }
    }
  }

  function handleInputBlur() {
    // On blur: if input has typing-invalid state, reset to last-good ISO's display.
    if (typingInvalid) {
      setInputText(selected ? toDisplayInput(selected) : '');
      setTypingInvalid(false);
    } else if (selected && inputText.trim()) {
      // Normalize formatting (eg "1/3/2024" -> "01/03/2024").
      setInputText(toDisplayInput(selected));
    }
  }

  const yearStart = (maxDate ?? new Date()).getFullYear() - 100;
  const yearEnd   = (maxDate ?? new Date()).getFullYear();
  const years: number[] = [];
  for (let y = yearEnd; y >= yearStart; y--) years.push(y);

  const showInvalidBorder = invalid || typingInvalid;

  return (
    <div ref={wrapperRef} className={cn('relative', className)}>
      <div
        className={cn(
          'w-full inline-flex items-center gap-2 rounded-lg border bg-background',
          'pl-3 pr-1 transition-all',
          'focus-within:ring-2 focus-within:ring-ring',
          'has-[input:disabled]:opacity-60 has-[input:disabled]:cursor-not-allowed',
          showInvalidBorder ? 'border-destructive' : 'border-border',
        )}
      >
        <input
          ref={inputRef}
          id={id}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          aria-label={ariaLabel}
          aria-invalid={showInvalidBorder || undefined}
          disabled={disabled}
          placeholder={placeholder}
          value={inputText}
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}
          onBlur={handleInputBlur}
          maxLength={10}
          className={cn(
            'flex-1 min-w-0 bg-transparent py-2.5 text-sm text-foreground',
            'placeholder:text-muted-foreground focus:outline-none',
            'disabled:cursor-not-allowed',
          )}
        />
        {calendar && (
          <button
            type="button"
            aria-haspopup="dialog"
            aria-expanded={open}
            aria-label="Open calendar"
            disabled={disabled}
            onClick={() => !disabled && setOpen((v) => !v)}
            className="shrink-0 h-8 w-8 rounded-md flex items-center justify-center text-muted-foreground hover:bg-muted transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed"
          >
            <CalendarIcon className="h-4 w-4" aria-hidden />
          </button>
        )}
      </div>

      {calendar && open && pos && createPortal(
        <div
          ref={popoverRef}
          role="dialog"
          aria-label="Calendar"
          // A modal Radix Dialog sets pointer-events:none on <body> and dismisses
          // on outside pointerdown, so keep this layer clickable and stop the
          // event before it reaches the dialog's document listener.
          onPointerDown={(e) => e.stopPropagation()}
          style={{
            position: 'fixed',
            left: pos.left,
            top: pos.top,
            bottom: pos.bottom,
            ...(maxH ? { maxHeight: maxH } : {}),
          }}
          className={cn(
            // Portaled to <body> above the Dialog (z-50) so an overflow-y-auto
            // modal cannot clip the calendar.
            'z-[60] w-[320px] max-w-[calc(100vw-2rem)] overflow-y-auto overscroll-contain pointer-events-auto',
            'rounded-2xl border border-border bg-background shadow-lg animate-slide-up-fade p-3',
            openUp ? 'origin-bottom' : 'origin-top',
          )}
        >
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              onClick={() => setViewMonth((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1))}
              className="h-8 w-8 rounded-lg flex items-center justify-center hover:bg-muted transition-colors"
              aria-label="Previous month"
            >
              <ChevronLeft className="h-4 w-4 text-muted-foreground" aria-hidden />
            </button>

            {/* Themed pickers rather than native <select>, whose popup list is
                drawn by the OS and cannot be styled. */}
            <div className="flex items-center gap-1.5">
              <FancySelect
                className="w-[7.25rem]"
                ariaLabel="Month"
                value={String(viewMonth.getMonth())}
                options={MONTHS_LONG.map((m, idx) => ({ value: String(idx), label: m }))}
                onChange={(v) =>
                  setViewMonth((d) => new Date(d.getFullYear(), Number(v), 1))
                }
              />
              <FancySelect
                className="w-[5.5rem]"
                ariaLabel="Year"
                value={String(viewMonth.getFullYear())}
                options={years.map((y) => ({ value: String(y), label: String(y) }))}
                onChange={(v) =>
                  setViewMonth((d) => new Date(Number(v), d.getMonth(), 1))
                }
              />
            </div>

            <button
              type="button"
              onClick={() => setViewMonth((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1))}
              className="h-8 w-8 rounded-lg flex items-center justify-center hover:bg-muted transition-colors"
              aria-label="Next month"
            >
              <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 mb-1">
            {DOW_SHORT.map((d) => (
              <span
                key={d}
                className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground text-center"
              >
                {d}
              </span>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {cells.map((cell, idx) => {
              const isSelected = !!selected && isSameDay(cell.date, selected);
              const isToday    = isSameDay(cell.date, today);
              const disabledD  = dayDisabled(cell.date);
              return (
                <button
                  key={idx}
                  type="button"
                  disabled={disabledD}
                  onClick={() => pick(cell.date)}
                  className={cn(
                    'h-8 w-full rounded-lg text-xs font-medium transition-all',
                    'hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    'disabled:opacity-25 disabled:cursor-not-allowed disabled:hover:bg-transparent',
                    !cell.current && 'text-muted-foreground/50',
                    cell.current  && 'text-foreground',
                  )}
                  style={
                    isSelected
                      ? {
                          backgroundColor: 'hsl(var(--palette-forest))',
                          color:           'hsl(var(--palette-cream))',
                        }
                      : isToday
                        ? { border: '1px solid hsl(var(--palette-amber))' }
                        : undefined
                  }
                  aria-label={cell.date.toDateString()}
                  aria-pressed={isSelected}
                >
                  {cell.date.getDate()}
                </button>
              );
            })}
          </div>

          <div className="flex items-center justify-between mt-2 pt-2 border-t border-border">
            <button
              type="button"
              onClick={() => {
                if (!dayDisabled(today)) pick(today);
                else setViewMonth(startOfMonth(today));
              }}
              className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
            >
              Today
            </button>
            {selected && (
              <button
                type="button"
                onClick={() => {
                  onChange('');
                  setInputText('');
                  setTypingInvalid(false);
                  setOpen(false);
                  inputRef.current?.focus();
                }}
                className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground hover:text-destructive transition-colors"
              >
                Clear
              </button>
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
